// CaseInbox: what needs a person over a search, as cases that can span
// sessions (lib/caseInbox.js): what a standard met, what an audit caught and
// rare acts no standard caught, joined across sessions by what links them,
// the most pressing first. Each case says how far it reaches (its sessions,
// the people and machines), why it is here, and what a dig made of it; a dig
// sends one investigator to read its sessions and what ran on the machines
// (lib/caseInboxWork.js). What a dig found and what the reader decided are
// kept by case, so the welcome page and every workspace read the same case.
//
// On the welcome page (`mode` "page") the cases are laid out as the Triage
// queue beside them lays out its rows: a strip over them (the investigation,
// and how much of what the standards let through a person should have seen,
// from audits that dig a random few of it), then the cases under headings
// saying where they stand, each row in the queue's columns. A case clicked
// opens under its row as a queue row does: the case, what digs found, and
// each of its sessions' findings (InvestigateCase's CaseBody), with the case
// one click from opening as a workspace.
//
// In a workspace's dock (`mode` "dock") it reads the sessions on the board
// (`steer`), the board's own case first (`focusCaseId`), each case opening
// in place: what the dig said, its notes (each opens its pin on the board,
// `onFocus`), and the sessions near it, which can be laid on the board to
// grow the case (`onLayOn`).
import { useCallback, useEffect, useId, useMemo, useState, useSyncExternalStore } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { LIT_FILL } from "./FlowBoard.jsx";
import { useTriageReads } from "./lenses/ReviewsBody.jsx";
import { SessionCard, machineOf, useSessionIdentity } from "./lenses/reviewSessions.jsx";
import { flaggedBy } from "./lenses/LiteStandards.jsx";
import { DigWord, NoteKind, PendingCases, usd } from "./lenses/InvestigateParts.jsx";
import { SwarmControls, WORKING_WORD } from "./lenses/InvestigateSwarm.jsx";
import { ACTION_CHIP, CASE_INSET, CountChip, Disclosure, FILE_LABEL, SeverityChip } from "./lenses/triageParts.jsx";
import { CaseBody, caseJobs, prefetchCase } from "./lenses/InvestigateCase.jsx";
import { useMyPermissions } from "../lib/permissions.js";
import { placeFindings } from "../lib/investigateFacts.js";
import { useInvestigation } from "../lib/investigateStore.js";
import { stopSwarm, useSwarm, workingOn } from "../lib/investigateSwarm.js";
import { askCoordinator, bindKeptCoordinator, canCoordinate, openCoordinator, startInvestigation } from "../lib/investigateCoordinator.js";
import { getTriageChatHost, subscribeTriageChatHost } from "../lib/triageChatStore.js";
import { getSuggestContext, subscribeSuggestContext } from "../lib/standardSuggest.js";
import { useWorkspaceCanvas } from "../lib/workspaceStore.js";
import { missedFlagOf, openMissedFlag, openStandardTuning, standardFromLabel } from "../lib/standardTune.js";
import { AUDIT_GROUPS, INBOX_SCOPE, auditReadout, formCases, isLetGo, latestOf } from "../lib/caseInbox.js";
import { INBOX_LABEL, auditInbox, decideInboxCase, digInboxCase, lastLookedAt, openInboxCase, provideInboxContext } from "../lib/caseInboxWork.js";
import { caseNotePrefix } from "../lib/investigateWorkspace.js";
import { caseCanvasId, previewTraceCanvas, traceCanvasKept, traceCanvasSnapshot, useTraceCanvases } from "../lib/traceCanvasStore.js";
import { NOTE_CLASS } from "../lib/ui.js";

// Cases listed before "show more", and how many more each time.
const SHOWN = { page: 25, dock: 12 };
const plural = (n, one, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const ORIGIN = {
  standard: ["Standard", "A standard you set was met."],
  audit: ["Audit", "An audit dug this though no standard escalated it, and said a person should see it."],
  rare: ["Rare", "It happened, it is high or worse, no standard caught it, and no other session in this search did this kind of act."],
  kept: ["Kept", "You opened, dug or decided this case; nothing else marks it now."],
};
const GROUP_WORDS = { slipped: "Slipped past the standards", allowed: "Allowed", contained: "Stayed contained" };

// How far a case reaches, in words: how many sessions and findings it holds
// (where more than one), and who ran them where (a person on a machine, or
// "2 people on 3 machines").
function reachText(view, machineFor) {
  return [
    view.sessions.length > 1 ? plural(view.sessions.length, "session") : "",
    view.items.length > 1 ? plural(view.items.length, "finding") : "",
    whoWhere(view, machineFor),
  ].filter(Boolean).join(", ");
}
function whoWhere(view, machineFor) {
  const machines = [...new Set(view.sessions.map((s) => machineFor(s)?.host).filter(Boolean))];
  const people = [...new Set(view.sessions.map((s) => machineFor(s)?.user).filter(Boolean))];
  const who = people.length > 1 ? plural(people.length, "person", "people") : people[0] || "";
  const where = machines.length > 1 ? plural(machines.length, "machine") : machines[0] || "";
  return who && where ? `${who} on ${where}` : who || where;
}

// Why a case is here, or what a dig made of it. `named`: what met it is
// said beside it (the page's row), so the standard's own why is enough.
function reasonText(view, { named = false } = {}) {
  const dig = view.dig;
  if (dig?.status === "done") return dig.why || view.account || "";
  if (dig?.status === "failed") return `The last dig failed${dig.error ? `: ${dig.error}` : ""}. Dig again to retry.`;
  if (view.origin === "standard") {
    const met = view.items.map((p) => p.verdict).find((v) => v?.escalates);
    if (named) return met?.standardWhy || view.account || "";
    const by = view.metBy.length ? `Met ${view.metBy.join(", ")}` : "Met a standard";
    return met?.standardWhy ? `${by}: ${met.standardWhy}` : by;
  }
  if (view.origin === "audit") {
    const caught = view.audits.find((a) => a.dig?.needsPerson === "yes");
    return `An audit's dig said a person should see this${caught?.dig?.why ? `: ${caught.dig.why}` : ""}`;
  }
  if (view.origin === "rare") return "No standard caught it, and no other session in this search did this kind of act";
  return "You kept this case";
}

// What a case's own investigator is doing, as a word: digging, or waiting.
function StatusWord({ view, activity }) {
  if (activity?.status === "running") {
    return <span role="status" data-case-working={activity.kind} className="text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--ink)] motion-safe:animate-pulse">{WORKING_WORD[activity.kind] || "Working"}</span>;
  }
  if (activity?.status === "queued") return <span data-case-queued="" className="text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--muted)]">Queued</span>;
  if (view.decision?.kind === "taken") return <span data-case-decision="taken" className="text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--ink)]">Taken</span>;
  if (view.decision?.kind === "let_go") return <span data-case-decision="let_go" className="text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--muted-soft)]">Let go</span>;
  if (view.dig || !view.likeLetGo) return <DigWord dig={view.dig} />;
  // Nobody has dug it, and it does what a case the reader let go did.
  const like = view.likeLetGo;
  return (
    <HoverTooltip label={`You let go \u201c${like.title || "a case"}\u201d, which did the same kind of act: ${like.kinds.join(", ")}.`} placement="top" wide>
      <span data-case-like-let-go={like.id} className="shrink-0 text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--muted)]">Like one let go</span>
    </HoverTooltip>
  );
}

function OriginWord({ origin }) {
  const [word, about] = ORIGIN[origin] || ORIGIN.kept;
  return (
    <HoverTooltip label={about} placement="top" wide>
      <span data-case-origin-word={origin} className="w-[56px] shrink-0 text-right text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--muted)]">{word}</span>
    </HoverTooltip>
  );
}

// The case's own controls: dig it, open it as a workspace, decide on it, and
// tune the standards by it (`tune`: `should` where no standard flagged a
// finding that one should have, `wrong` where one flagged what it should
// not have, each opening the standards' tuning chat). On a page row they
// stand over the row's right end while the pointer or the keyboard is on the
// row, on the row's own hover ground, so a row at rest keeps no room for
// them.
const OVER_ROW = "pointer-events-none absolute inset-y-0 right-0 pl-[12px] pr-[8px] opacity-0 transition-opacity group-hover/case:pointer-events-auto group-hover/case:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100";
function CaseActions({ view, activity, onDig, onWorkspace = null, onDecide, tune = null, always = false, dig = true }) {
  const busy = activity?.status === "running" || activity?.status === "queued";
  const decided = view.decision?.kind || null;
  return (
    <span data-case-actions="" className={`flex shrink-0 items-center gap-[4px] ${always ? "" : OVER_ROW}`} style={always ? undefined : { background: LIT_FILL }}>
      {busy || !dig ? null : (
        <HoverTooltip label="Sends one investigator to read the case's sessions and what ran on their machines, and say whether a person should look. Its notes are pinned at their moments." placement="top" wide>
          <button type="button" data-case-dig="" onClick={() => onDig(view)} className={ACTION_CHIP}>{view.dig?.status === "done" ? "Dig again" : "Dig"}</button>
        </HoverTooltip>
      )}
      {onWorkspace ? (
        <HoverTooltip label="Opens the case as a workspace: its sessions side by side, its findings pinned open, and what digs found at their moments." placement="top" wide>
          <button type="button" data-case-workspace="" onClick={() => onWorkspace(view)} className={ACTION_CHIP}>Workspace</button>
        </HoverTooltip>
      ) : null}
      {tune?.should ? (
        <HoverTooltip label="No standard flagged this, and one should have: opens a chat that tunes the standards to catch it." placement="top" wide>
          <button type="button" data-case-should-flag="" onClick={tune.should} className={ACTION_CHIP}>Should flag</button>
        </HoverTooltip>
      ) : null}
      {tune?.wrong ? (
        <HoverTooltip label={`${tune.wrongLabel} flagged this, and shouldn't have: opens a chat that tunes it.`} placement="top" wide>
          <button type="button" data-case-wrong-flag="" onClick={tune.wrong} className={ACTION_CHIP}>Wrong flag</button>
        </HoverTooltip>
      ) : null}
      {decided ? (
        <button type="button" data-case-undecide="" onClick={() => onDecide(view, null)} className={ACTION_CHIP}>Undo</button>
      ) : (
        <>
          <HoverTooltip label="You take it on: it stays at the top of the cases, marked as taken, here and in every workspace." placement="top">
            <button type="button" data-case-take="" onClick={() => onDecide(view, "taken")} className={ACTION_CHIP}>Take it</button>
          </HoverTooltip>
          <HoverTooltip label="Nothing to do: it moves to the cases you let go, here and in every workspace." placement="top">
            <button type="button" data-case-let-go="" onClick={() => onDecide(view, "let_go")} className={ACTION_CHIP}>Let go</button>
          </HoverTooltip>
        </>
      )}
    </span>
  );
}

// Where a case stands, as the welcome page heads its cases: in the order the
// cases are listed (lib/caseInbox.js compareInbox), so each heads a run of
// them. A row says where it stands only by its heading.
const STANDING = {
  taken: "Taken",
  follow: "Follow up",
  unclear: "Unclear",
  undug: "Not dug yet",
  clear: "Can be let go",
  let_go: "Let go",
};
const DUG = { yes: "follow", unsure: "unclear", no: "clear" };
const standingOf = (view) => (view.decision?.kind === "taken" ? "taken"
  : view.decision?.kind === "let_go" ? "let_go"
    : view.dig?.status === "done" ? DUG[view.dig.needsPerson] || "undug" : "undug");
// The cases' runs by where they stand, in the order listed.
function byStanding(views) {
  const out = [];
  for (const view of views) {
    const standing = standingOf(view);
    if (out.at(-1)?.standing !== standing) out.push({ standing, views: [] });
    out.at(-1).views.push(view);
  }
  return out;
}
// A heading as the Triage queue heads a day.
const HEADING = "px-[6px] pt-[12px] pb-[4px] text-[10px] leading-[1.2] tracking-[0.08em] uppercase text-[color:var(--muted)]";

// What a page row says of itself beside its heading: an investigator at work
// on it, a dig that failed, or that it is like a case the reader let go.
function RowWord({ view, activity }) {
  if (activity?.status === "running" || activity?.status === "queued") return <StatusWord view={view} activity={activity} />;
  if (view.dig?.status === "failed") return <DigWord dig={view.dig} />;
  if (!view.dig && !view.decision && view.likeLetGo) return <StatusWord view={view} activity={null} />;
  return null;
}

// Its sessions and who ran them where, in the column the Triage queue gives a
// row's session and machine: one session by its name, else how many, over
// who ran them on what.
function CaseReach({ view, labels, machineFor }) {
  const one = view.sessions.length === 1 ? view.sessions[0] : null;
  const label = one ? labels?.get(one) || null : null;
  const where = whoWhere(view, machineFor);
  return (
    <span data-case-reach="" className="grid w-[170px] shrink-0 gap-[2px]">
      {one ? (
        <OverflowTooltip
          text={label || `Session ${one.slice(0, 8)}`}
          always
          tooltipContent={<SessionCard id={one} label={label} machine={machineFor(one)} />}
          className="min-w-0 truncate text-[11px] leading-[1.15] text-[color:var(--muted)]"
        />
      ) : (
        <span className="truncate text-[11px] leading-[1.15] text-[color:var(--muted)]">{plural(view.sessions.length, "session")}</span>
      )}
      {where ? <span className="truncate text-[9px] leading-[1.2] text-[color:var(--muted-soft)]">{where}</span> : null}
    </span>
  );
}

// What put a case here, in the column the Triage queue says which standards
// flagged a row: the standards by name, an audit, its rarity, or the reader.
const RAISED = { audit: "Caught by an audit", rare: "A rare act", kept: "Kept by you" };
function CaseRaisedBy({ view, names }) {
  const [, about] = ORIGIN[view.origin] || ORIGIN.kept;
  const text = view.origin === "standard"
    ? (names.length ? `Flagged by ${names.join(", ")}` : "Flagged by a standard")
    : RAISED[view.origin] || RAISED.kept;
  return (
    <HoverTooltip label={`${text}. ${about}`} placement="top" wide>
      <span data-case-origin-word={view.origin} className="flex w-[180px] min-w-0 shrink-0">
        <span className="min-w-0 truncate text-[11px] leading-[15px] text-[color:var(--muted)]">{text}</span>
      </span>
    </HoverTooltip>
  );
}

// A case on the welcome page, in the Triage queue's columns: its severity,
// its title over why it is here, how many findings it holds, its sessions and
// who ran them where, and what put it here. A click opens it under its row,
// as a queue row opens: the case, what digs found, and each session's
// findings, with the case one click from opening as a workspace (there, or
// over the row's end with the case's other controls).
function PageRow({ view, activity, labels, machineFor, raisedBy, open, onToggle, conductName, onOpenSession, onWorkspace, onDig, onDecide, tune = null, fresh = false }) {
  const line = activity?.status === "running" ? activity.now : reasonText(view, { named: true });
  const quiet = isLetGo(view);
  const jobs = useMemo(() => caseJobs(view), [view]);
  return (
    <div
      data-case-row={view.id}
      data-case-origin={view.origin}
      data-case-standing={standingOf(view)}
      data-case-open={open ? "" : undefined}
      className={`grid min-w-0 border-t border-[color:var(--line-soft)] transition-colors ${open ? "bg-[color:var(--panel)]" : ""}`}
    >
      <div
        className={`@container group/case relative flex min-w-0 items-center transition-colors duration-150 ${open ? "" : "hover:bg-[var(--row-hover)]"}`}
        style={{ "--row-hover": LIT_FILL }}
      >
        <div
          data-case-row-open=""
          onClick={onToggle}
          onMouseEnter={() => prefetchCase(jobs)}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-x-[12px] px-[6px] py-[8px]"
        >
          <Disclosure data-case-toggle="" open={open} label={open ? "Close the case" : "Open the case"} onToggle={onToggle} />
          <SeverityChip severity={view.severity} />
          <span className="grid min-w-0 flex-1 gap-[2px]">
            <OverflowTooltip text={view.title} className={`min-w-0 truncate text-[12px] leading-[16px] ${quiet ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`} />
            {line ? <OverflowTooltip text={line} className="min-w-0 truncate text-[11px] leading-[14px] text-[color:var(--muted)]" /> : null}
          </span>
          <span className="flex shrink-0 items-center gap-x-[10px]">
            {fresh ? (
              <HoverTooltip label="It has findings reviewed since you last looked at the cases." placement="top">
                <span data-case-new="" className="shrink-0 text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--ink)]">New</span>
              </HoverTooltip>
            ) : null}
            <RowWord view={view} activity={activity} />
            <CountChip count={view.items.length} />
            {/* The queue drops this column under 760px; a case's row has no
                chat chip at its end, so it keeps it 128px further in. */}
            <span className="hidden @min-[600px]:flex">
              <CaseReach view={view} labels={labels} machineFor={machineFor} />
            </span>
            <CaseRaisedBy view={view} names={raisedBy(view)} />
          </span>
        </div>
        {open ? null : <CaseActions view={view} activity={activity} onDig={onDig} onWorkspace={onWorkspace} onDecide={onDecide} tune={tune} />}
      </div>
      {open ? (
        <>
          <CaseBody
            view={view} activity={activity} labels={labels} conductName={conductName}
            onDig={onDig} onWorkspace={onWorkspace} onOpenSession={onOpenSession}
          />
          {/* What the row's end offers while it is closed, but the dig, which
              the case's own dig box asks: deciding on it and tuning the
              standards by it, under all it holds. */}
          <div data-case-body-actions="" className="pb-[12px]" style={{ paddingLeft: CASE_INSET, paddingRight: 6 }}>
            <CaseActions view={view} activity={activity} onDig={onDig} onDecide={onDecide} tune={tune} always dig={false} />
          </div>
        </>
      ) : null}
    </div>
  );
}

// The dock's look: the Walkthrough tab's rows and words.
const META = "text-[11px] leading-[1.3]";
const EYEBROW = "text-[10px] uppercase tracking-[0.07em] leading-[1.2] text-[color:var(--eyebrow)]";

// A case in a workspace's dock: its head opens it in place.
// The pin a case's note stands as on the board, where it names a moment.
const pinOf = (view, n) => (n.session && n.messageId ? `${caseNotePrefix(view.id)}${n.id}` : null);

function DockCase({ view, open, here = false, onBoard, activity, labels, machineFor, onToggle, onDig, onWorkspace, onDecide, onFocus, onLayOn, tune = null, litPins = null, onPoint = null }) {
  const [question, setQuestion] = useState("");
  // A pin the board's pointer is on lights its note, and its case.
  const caseLit = !!litPins?.size && view.notes.some((n) => litPins.has(pinOf(view, n)));
  const line = activity?.status === "running" ? activity.now : [reachText(view, machineFor), reasonText(view)].filter(Boolean).join(" \u00b7 ");
  const send = () => { onDig(view, question.trim()); setQuestion(""); };
  const busy = activity?.status === "running" || activity?.status === "queued";
  const name = (s) => labels.get(s) || `Session ${s.slice(0, 8)}`;
  return (
    <div data-case-row={view.id} data-case-origin={view.origin} data-case-here={here ? "" : undefined} className="group/case grid min-w-0 border-b border-[color:var(--line-soft)]">
      <div data-lit={caseLit ? "" : undefined} className={`flex min-w-0 items-center gap-x-[8px] pr-[9px] transition-colors ${open || caseLit ? "bg-[color:var(--gray-fill)]" : "hover:bg-[color:var(--gray-fill)]"}`}>
        <button type="button" data-case-open="" aria-expanded={open} onClick={onToggle} className="flex min-w-0 flex-1 cursor-pointer items-center gap-x-[8px] py-[7px] pl-[9px] text-left outline-none">
          <SeverityChip severity={view.severity} />
          <span className="grid min-w-0 flex-1 gap-[2px]">
            <span className="flex min-w-0 items-baseline gap-[6px]">
              {here ? <span className={`${EYEBROW} shrink-0`}>This case</span> : null}
              <OverflowTooltip text={view.title} className={`min-w-0 truncate text-[12px] leading-[1.35] ${isLetGo(view) ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`} />
            </span>
            <OverflowTooltip text={line} className={`${META} min-w-0 truncate text-[color:var(--muted)]`} />
          </span>
        </button>
        <StatusWord view={view} activity={activity} />
        <OriginWord origin={view.origin} />
      </div>
      {open ? (
        <div data-case-body="" className="grid min-w-0 gap-[8px] px-[9px] pt-[4px] pb-[10px]">
          {view.account ? <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]" data-case-account="">{view.account}</p> : null}
          {view.dig?.status === "done" ? (
            <span className={`${META} text-[color:var(--muted)]`} data-case-dug="">
              <DigWord dig={view.dig} />{` ${view.dig.why || ""}`}{view.dig.costUsd ? ` \u00b7 ${usd(view.dig.costUsd)}` : ""}
            </span>
          ) : null}
          {busy ? <span role="status" className={`${META} text-[color:var(--ink)]`}>{activity.status === "running" ? `${WORKING_WORD[activity.kind] || "Working"}: ${activity.now}` : "Waiting its turn behind the investigators at work"}</span> : (
            <span className="flex min-w-0 items-center gap-[6px]">
              <input
                data-case-question=""
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                maxLength={320}
                placeholder={view.dig?.status === "done" ? "Ask something the last dig left open (optional)" : "What should the dig find out? (optional)"}
                aria-label="Question for the dig"
                className={`${META} min-w-0 flex-1 rounded-[2px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[7px] py-[4px] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--line-strong)]`}
              />
              <button type="button" data-case-dig-send="" onClick={send} className={ACTION_CHIP}>{view.dig?.status === "done" ? "Dig again" : "Dig"}</button>
            </span>
          )}
          {view.notes.length ? (
            <ul data-case-notes="" className="m-0 grid list-none p-0">
              {view.notes.map((n) => {
                const pin = pinOf(view, n);
                const lit = !!pin && !!litPins?.has(pin);
                // Pointed at, its pin lights on the board, as a Walkthrough moment's does.
                const point = (on) => () => { if (pin) onPoint?.(on ? pin : null); };
                return (
                <li key={n.id} className="grid min-w-0 border-t border-[color:var(--line-soft)] first:border-t-0">
                  <button
                    type="button"
                    data-case-note={n.kind}
                    data-lit={lit ? "" : undefined}
                    disabled={!n.session}
                    onClick={() => onFocus?.({ session: n.session, messageId: n.messageId || null, noteId: pin })}
                    onMouseEnter={point(true)}
                    onMouseLeave={point(false)}
                    onFocus={point(true)}
                    onBlur={point(false)}
                    className={`grid min-w-0 cursor-pointer gap-[2px] px-[4px] py-[5px] text-left transition-colors disabled:cursor-default ${lit ? "bg-[color:var(--gray-fill)]" : "hover:bg-[color:var(--gray-fill)]"}`}
                  >
                    <NoteKind kind={n.kind} />
                    <span className="min-w-0 text-[11px] leading-[1.45] text-[color:var(--ink)] [overflow-wrap:anywhere]">{n.text}</span>
                  </button>
                </li>
                );
              })}
            </ul>
          ) : null}
          {view.nearby.length ? (
            <div data-case-nearby="" className="grid min-w-0 gap-[3px]">
              <span className={EYEBROW}>Near this case</span>
              {view.nearby.map((n) => (
                <span key={n.session} className="flex min-w-0 items-baseline gap-[8px]">
                  <span className={`${META} min-w-0 flex-1 truncate text-[color:var(--muted)]`}>
                    <span className="text-[color:var(--ink)]">{name(n.session)}</span>{n.why ? ` \u00b7 ${n.why}` : ""}
                  </span>
                  {onBoard.has(n.session) ? (
                    <button type="button" onClick={() => onFocus?.({ session: n.session })} className={ACTION_CHIP}>Show</button>
                  ) : onLayOn ? (
                    <HoverTooltip label="Lays the session on this workspace beside the case's. On the case's own workspace, it joins the case." placement="top" wide>
                      <button type="button" data-case-lay-on={n.session} onClick={() => onLayOn(n.session)} className={ACTION_CHIP}>Lay on</button>
                    </HoverTooltip>
                  ) : null}
                </span>
              ))}
            </div>
          ) : null}
          <CaseActions view={view} activity={activity} onDig={(v) => onDig(v, "")} onWorkspace={here ? null : onWorkspace} onDecide={onDecide} tune={tune} always />
        </div>
      ) : null}
    </div>
  );
}

// How much of what the standards let through a person should have seen,
// from the random audits, and the reader's way to audit more.
// How much of what the standards let through a person should have seen, as a
// line of the strip over the welcome page's cases, its audit at the end.
function MissedReadout({ readout, onAudit, reading }) {
  const t = readout.total;
  const groups = readout.groups.filter((g) => g.findings || g.sampled);
  const detail = (
    <span className="grid gap-[4px]">
      <span>An audit digs a few findings at random from each group the standards did not escalate, and asks whether a person should have seen them. How many should have estimates what the standards miss. Each audit also digs the rare acts nobody has.</span>
      {readout.groups.map((g) => (
        <span key={g.group} className="text-[color:var(--muted)]">
          {`${GROUP_WORDS[g.group]}: ${g.dug ? `${g.caught} of ${g.dug} dug` : "none dug"}${g.pending ? `, ${g.pending} waiting` : ""}; ${plural(g.findings, "finding")} in this search`}
        </span>
      ))}
    </span>
  );
  const letThrough = AUDIT_GROUPS.reduce((n, g) => n + (readout.groups.find((x) => x.group === g)?.findings || 0), 0);
  return (
    <div data-case-missed="" className="flex min-w-0 flex-wrap items-center gap-x-[10px] gap-y-[6px] border-t border-[color:var(--line)] px-[10px] py-[7px]">
      <HoverTooltip label={detail} placement="top" wide maxWidth={380}>
        <span className="inline-flex min-w-0 cursor-help flex-wrap items-baseline gap-x-[10px]">
          <span className={FILE_LABEL}>Missed by the standards</span>
          <span className="text-[12px] leading-[1.3] text-[color:var(--ink)]">
            <span data-case-missed-rate="">{t.dug ? `${t.caught} of ${t.dug} dug at random needed a person` : "Not measured yet"}</span>
            <span className="text-[color:var(--muted)]">
              {t.pending ? ` \u00b7 ${t.pending} waiting` : ""}
              {` \u00b7 ${plural(letThrough, "finding")} let through in this search`}
            </span>
          </span>
        </span>
      </HoverTooltip>
      <button type="button" data-case-audit="" disabled={reading || !groups.length} onClick={onAudit} className={`${ACTION_CHIP} ml-auto disabled:opacity-50`}>
        {t.sampled ? "Audit more" : "Audit a sample"}
      </button>
    </div>
  );
}

/**
 * The cases over what useTriageReads read (`t`). `scopeLabel`: what the
 * search is called in the empty line. `onCount(n, fresh)` hears how many
 * cases are open once read (null until then), and how many of them are new
 * since the reader last looked. Dock: `focusCaseId`, `onBoard` (the
 * board's sessions), `onFocus({ session, messageId, noteId })` (`noteId`: the
 * pin a note stands as on the board), `onLayOn(session)`, `pointed` (the pins
 * the board's pointer is on) and `onPoint(pin)` (lights a pin, or none).
 */
export function CaseInboxBody({
  t, mode = "page", scopeLabel = "this search", onCount = null,
  focusCaseId = null, onBoard = null, onFocus = null, onLayOn = null, pointed = null, onPoint = null,
}) {
  const dock = mode === "dock";
  const investigation = useInvestigation(INBOX_SCOPE);
  const swarm = useSwarm(INBOX_SCOPE);
  // The inbox's case boards by what they hold, so the cases are formed again
  // when a session is laid on or taken off one, not as a board is worked.
  const boards = useTraceCanvases();
  const boardsKey = useMemo(() => boards
    .filter((b) => b.id.startsWith(`case:${INBOX_SCOPE}:`))
    .map((b) => {
      const c = traceCanvasSnapshot(b.id);
      return `${b.id}=${[c.anchor, ...c.layered.map((x) => x.id)].join(",")}`;
    })
    .join("|"), [boards]);
  const standards = t.liteStd.lib.items;
  // With one standard kept, the answers carry its digest but not its name: a
  // met finding names the standard that met it either way.
  const verdicts = useMemo(() => {
    const byDigest = new Map(standards.map((x) => [x.digest, `${x.name} v${x.version}`]));
    const out = {};
    for (const [id, v] of Object.entries(t.liteStd.effective || {})) {
      out[id] = v?.escalates && !v.metBy?.length && byDigest.has(v.standardDigest) ? { ...v, metBy: [byDigest.get(v.standardDigest)] } : v;
    }
    return out;
  }, [t.liteStd.effective, standards]);
  const placed = useMemo(() => (t.read ? placeFindings(t.lite.pool, {
    verdicts, checks: investigation.checks, corrections: investigation.corrections, asked: investigation.asked, standards: standards.length,
  }) : []), [t.read, t.lite.pool, verdicts, investigation.checks, investigation.corrections, investigation.asked, standards.length]);
  // A case's board holds what the reader laid on it: the case grows by it.
  const boardOf = useCallback((id) => {
    const key = caseCanvasId(INBOX_SCOPE, id);
    if (!traceCanvasKept(key)) return null;
    const c = traceCanvasSnapshot(key);
    return [c.anchor, ...c.layered.map((x) => x.id)].filter(Boolean);
  }, [boardsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const cases = useMemo(() => (t.read ? formCases({ placed, map: t.lite.map, investigation, boardOf, rare: !dock }) : null), [t.read, placed, t.lite.map, investigation, boardOf, dock]);
  const ordered = useMemo(() => {
    if (!cases || !focusCaseId) return cases;
    const here = cases.find((c) => c.id === focusCaseId);
    return here ? [here, ...cases.filter((c) => c !== here)] : cases;
  }, [cases, focusCaseId]);
  const open = useMemo(() => (ordered || []).filter((c) => !isLetGo(c)), [ordered]);
  // What digs said can be let go, that the reader has not decided on.
  const cleared = useMemo(() => open.filter((c) => !c.decision && c.dig?.status === "done" && c.dig.needsPerson === "no"), [open]);
  const letGo = useMemo(() => (ordered || []).filter(isLetGo), [ordered]);

  const [more, setMore] = useState(0);
  const [showLetGo, setShowLetGo] = useState(false);
  const shownOpen = useMemo(() => open.slice(0, SHOWN[mode] + more), [open, mode, more]);
  const shownSessions = useMemo(() => [...new Set([...shownOpen, ...(showLetGo ? letGo : [])].flatMap((c) => [...c.sessions, ...c.nearby.map((n) => n.session), ...c.related.map((r) => r.session)]))].sort(), [shownOpen, letGo, showLetGo]);
  const shownEndpoints = useMemo(() => [...new Set(shownOpen.flatMap((c) => c.endpoints))].sort(), [shownOpen]);
  const window = t.lite.readWindow || t.ids.window;
  const { labels, machineFor } = useSessionIdentity({ map: t.lite.map, sessions: shownSessions, endpoints: shownEndpoints, window });
  const nameOf = useMemo(() => ({ session: (id) => labels.get(id), machine: (endpoint) => machineOf(endpoint)?.host || null, conduct: t.lite.conductName }), [labels, t.lite.conductName]);

  // What this page read, for what investigators read after a dig.
  const hostId = useId();
  useEffect(() => {
    if (t.read && cases) provideInboxContext(hostId, { pool: t.lite.pool, map: t.lite.map, verdicts, standards, window, nameOf, cases, primary: !dock });
  }, [hostId, t.read, t.lite.pool, t.lite.map, verdicts, standards, window, nameOf, cases, dock]);
  useEffect(() => () => provideInboxContext(hostId, null), [hostId]);
  // What is new since the reader last looked at the cases (the welcome
  // page's): a case with findings reviewed since, not let go.
  const [seenAt] = useState(() => (dock ? null : lastLookedAt()));
  const isNew = useCallback((view) => seenAt != null && !isLetGo(view) && latestOf(view) > seenAt, [seenAt]);
  const count = cases ? open.length : null;
  const fresh = cases ? open.filter(isNew).length : 0;
  useEffect(() => { onCount?.(count, fresh); }, [count, fresh, onCount]);

  const [opened, setOpened] = useState(() => new Set(focusCaseId ? [focusCaseId] : []));
  useEffect(() => {
    if (focusCaseId) setOpened((cur) => (cur.has(focusCaseId) ? cur : new Set(cur).add(focusCaseId)));
  }, [focusCaseId]);
  const toggle = useCallback((id) => setOpened((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }), []);
  // The chat console, where one is mounted: it holds the investigation's
  // chat, which the reader starts, steers and talks to, and where
  // investigators show as they work. On the welcome page what the reader asks
  // for here (dig this, audit) is said there as theirs; a workspace's tab
  // sends its digs straight.
  const host = useSyncExternalStore(subscribeTriageChatHost, getTriageChatHost, () => null);
  const coordinate = !dock && canCoordinate(host);
  // A coordinator this browser kept hears from investigators again after a reload.
  useEffect(() => { if (coordinate) bindKeptCoordinator({ host, scopeKey: INBOX_SCOPE }); }, [coordinate, host]);
  const viaChat = useCallback((prompt) => coordinate && !!askCoordinator({ host, scopeKey: INBOX_SCOPE, scopeLabel: INBOX_LABEL, prompt }), [coordinate, host]);
  // On a workspace, the sessions its board found sharing content (a file, a
  // token, a URL, a commit) with its traces are leads a dig may read.
  const board = useWorkspaceCanvas();
  const boardLeads = useMemo(() => (dock ? (board?.related || []).filter((r) => r?.id && !r.kgOnly).map((r) => ({
    session: String(r.id).toLowerCase(),
    why: `shares ${plural(Number(r.shared) || 0, "string")} with this workspace's traces${r.same ? ", on the same machine" : ""}`,
  })) : []), [dock, board?.related]);
  const onDig = useCallback((view, question = "") => {
    if (viaChat(`Dig into \u201c${view.title}\u201d${question ? `: ${question}` : "."} (case ${view.id})`)) return;
    digInboxCase(view, { question, window, nameOf, leads: boardLeads });
  }, [viaChat, window, nameOf, boardLeads]);
  const onWorkspace = useCallback((view) => openInboxCase(view, { titleOf: (s) => labels.get(s) || "" }), [labels]);
  // A session of a case, previewed on its own as the Triage queue previews
  // one, at the message asked for (a finding's, a note's) where there is one.
  const openSession = useCallback(({ session, message = null }) => {
    previewTraceCanvas({ id: session, title: labels.get(session) || "", message });
  }, [labels]);
  const onDecide = useCallback((view, kind) => decideInboxCase(view, kind), []);
  // What a case can tune the standards by, where a chat can open and the
  // Triage reads offer the standards (the welcome page's): a finding no
  // standard flagged that the standards were asked about, and the standard
  // that flagged the case's findings.
  const tuneCtx = useSyncExternalStore(subscribeSuggestContext, getSuggestContext, () => null);
  const canTune = typeof host?.ask === "function" && !!tuneCtx?.sensitive;
  const tuningOf = useCallback((view) => {
    if (!canTune) return null;
    const missed = view.items.find((p) => missedFlagOf(tuneCtx, p.finding.id));
    const label = view.metBy[0] || null;
    const item = label ? standardFromLabel(tuneCtx, label) : null;
    const wrongIds = item ? view.items.filter((p) => p.verdict?.escalates && (p.verdict.metBy || []).includes(label)).map((p) => p.finding.id) : [];
    if (!missed && !wrongIds.length) return null;
    const open = (fn) => () => {
      try { fn(); } catch { /* offered only where a chat can open */ }
    };
    return {
      should: missed ? open(() => openMissedFlag({ host, findingId: missed.finding.id })) : null,
      wrong: wrongIds.length ? open(() => openStandardTuning({ host, standardId: item.standardId, wrongIds })) : null,
      wrongLabel: label,
    };
  }, [canTune, tuneCtx, host]);
  const onAudit = useCallback(() => {
    if (viaChat("Audit a sample of what the standards let through, and dig the rare acts nobody has.")) return;
    auditInbox({ placed, map: t.lite.map, cases: cases || [], window, nameOf });
  }, [viaChat, placed, t.lite.map, cases, window, nameOf]);
  const onStart = useCallback(() => startInvestigation({ scopeKey: INBOX_SCOPE, scopeLabel: INBOX_LABEL, host: coordinate ? host : null }), [coordinate, host]);
  const onTalk = useCallback(() => { if (coordinate) openCoordinator({ host, scopeKey: INBOX_SCOPE, scopeLabel: INBOX_LABEL }); }, [coordinate, host]);
  const readout = useMemo(() => auditReadout({ investigation, placed }), [investigation, placed]);
  // On a workspace, what the standards let through on its board that no
  // audit or case holds yet.
  const letThroughHere = useMemo(() => {
    if (!dock) return 0;
    const held = new Set([...investigation.cases.flatMap((c) => c.findings), ...(cases || []).flatMap((c) => c.findingIds.map((id) => String(id).toLowerCase()))]);
    return placed.filter((p) => AUDIT_GROUPS.includes(p.group) && !held.has(String(p.finding.id).toLowerCase())).length;
  }, [dock, investigation.cases, cases, placed]);
  const boardSet = useMemo(() => new Set((onBoard || []).map((s) => String(s).toLowerCase())), [onBoard]);
  // The standards that flagged a case's findings, by name, as the Triage
  // queue names a row's; else the standards its verdicts say met it.
  const raisedBy = useCallback((view) => {
    const names = flaggedBy(view.items.map((p) => p.finding), t.liteStd).map((x) => x.name);
    return names.length ? names : view.metBy;
  }, [t.liteStd]);
  const litPins = useMemo(() => new Set(pointed || []), [pointed]);

  const row = (view) => (dock ? (
    <DockCase
      key={view.id}
      view={view}
      open={opened.has(view.id)}
      here={view.id === focusCaseId}
      onBoard={boardSet}
      activity={workingOn(swarm, view.id)}
      labels={labels}
      machineFor={machineFor}
      onToggle={() => toggle(view.id)}
      onDig={onDig}
      onWorkspace={onWorkspace}
      onDecide={onDecide}
      onFocus={onFocus}
      onLayOn={onLayOn}
      tune={tuningOf(view)}
      litPins={litPins}
      onPoint={onPoint}
    />
  ) : (
    <PageRow
      key={view.id}
      view={view}
      activity={workingOn(swarm, view.id)}
      labels={labels}
      machineFor={machineFor}
      raisedBy={raisedBy}
      open={opened.has(view.id)}
      onToggle={() => toggle(view.id)}
      conductName={t.lite.conductName}
      onOpenSession={openSession}
      onWorkspace={onWorkspace}
      onDig={onDig}
      onDecide={onDecide}
      tune={tuningOf(view)}
      fresh={isNew(view)}
    />
  ));

  const blocked = t.conductState.status === "hidden" ? "Cases need permission to read sensitive AI session data."
    : t.conductState.status === "denied" ? "You don't have access to the sessions this search reaches."
      : t.conductState.status === "unsupported" ? "Cases can't be read for this search."
        : t.conductState.status === "error" ? "Couldn\u2019t read the findings in this search."
          : t.failed ? "Couldn\u2019t read what Triage checked." : null;
  // The investigation's controls. On the welcome page, always: start it (in
  // its chat, where the reader steers it, or straight to a dig of every case
  // nobody has dug or decided), and while it runs, what is at work, what it
  // has spent, the chat and stop. In a workspace's tab, while investigators
  // are at work or waiting; after, what the digs cost.
  const working = !!swarm?.on && swarm.investigators.some((i) => i.status === "running" || i.status === "queued");
  const dug = open.filter((c) => c.dig?.status === "done").length;
  const controls = (
    <SwarmControls
      swarm={swarm} canCoordinate={coordinate} reading={!t.read} spentHere={investigation.spentUsd}
      onStart={onStart} onStop={() => stopSwarm(INBOX_SCOPE)} onTalk={onTalk}
      strip={!dock} idle={open.length ? `${dug.toLocaleString()} of ${plural(open.length, "case")} dug` : "Nothing to dig"}
    />
  );
  const spend = !dock || working ? controls : swarm?.spentUsd > 0 ? (
    <span data-case-spent="" className={NOTE_CLASS}>{`${usd(swarm.spentUsd)} spent digging since this page opened`}</span>
  ) : null;
  // On the page, what the investigation is doing and what the standards miss,
  // in a strip over the cases as the Triage queue keeps its standards.
  const strip = dock ? null : (
    <div data-case-strip="" className="grid overflow-hidden rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)]">
      <div className="flex min-w-0 flex-wrap items-center gap-x-[10px] gap-y-[6px] px-[10px] py-[7px]">
        <span className={FILE_LABEL}>Investigation</span>
        {controls}
      </div>
      <MissedReadout readout={readout} onAudit={onAudit} reading={!t.read} />
    </div>
  );

  if (blocked) return <div data-case-inbox={mode} className={`${NOTE_CLASS} ${dock ? "px-[9px] py-[8px]" : "py-[12px]"}`}>{blocked}</div>;
  if (!cases) {
    return (
      <div data-case-inbox={mode} aria-busy="true" className={dock ? "" : "py-[6px]"}>
        <PendingCases rows={dock ? 2 : 3} />
      </div>
    );
  }
  return (
    <div data-case-inbox={mode} className={dock ? "grid min-w-0" : "grid min-w-0 pt-[12px]"}>
      {dock ? (spend ? <div className="border-b border-[color:var(--line-soft)] px-[9px] py-[5px]">{spend}</div> : null) : strip}
      {open.length ? (
        dock ? (
          <div data-case-list="" className="grid min-w-0">{shownOpen.map(row)}</div>
        ) : (
          <div data-case-list="" className="grid min-w-0">
            {byStanding(shownOpen).map((g, i) => (
              <div key={`${g.standing}:${i}`} data-case-standing-group={g.standing} className="grid min-w-0">
                <span data-case-group={g.standing} className={HEADING}>{STANDING[g.standing]}</span>
                {g.views.map(row)}
              </div>
            ))}
          </div>
        )
      ) : (
        <span data-case-none="" className={`${NOTE_CLASS} ${dock ? "px-[9px] py-[8px]" : "py-[10px]"}`}>
          {dock ? "Nothing on this workspace needs a person: no standard met, audit caught or case kept here." : `Nothing in ${scopeLabel} needs a person: no standard met, no audit caught anything, and no rare act stands out.`}
        </span>
      )}
      {open.length > shownOpen.length || letGo.length || cleared.length ? (
        <span className={`flex flex-wrap items-center gap-[8px] ${dock ? "px-[9px] py-[6px]" : "pt-[8px]"}`}>
          {cleared.length ? (
            <HoverTooltip label="Moves every case a dig said can be let go, that you have not decided on, to the cases you let go." placement="top" wide>
              <button type="button" data-case-clear="" onClick={() => { for (const c of cleared) decideInboxCase(c, "let_go"); }} className={ACTION_CHIP}>
                Let go the {plural(cleared.length, "case")} a dig cleared
              </button>
            </HoverTooltip>
          ) : null}
          {open.length > shownOpen.length ? (
            <button type="button" data-case-more="" onClick={() => setMore((n) => n + SHOWN[mode])} className={ACTION_CHIP}>
              Show {Math.min(SHOWN[mode], open.length - shownOpen.length).toLocaleString()} more of {(open.length - shownOpen.length).toLocaleString()}
            </button>
          ) : null}
          {letGo.length ? (
            <button type="button" data-case-let-go-list="" aria-expanded={showLetGo} onClick={() => setShowLetGo((v) => !v)} className={ACTION_CHIP}>
              {showLetGo ? "Hide" : "Show"} {plural(letGo.length, "case")} you let go
            </button>
          ) : null}
        </span>
      ) : null}
      {showLetGo && letGo.length ? (
        <div data-case-let-go-rows="" className="grid min-w-0">
          {dock ? null : <span data-case-group="let_go" className={HEADING}>{STANDING.let_go}</span>}
          {letGo.map(row)}
        </div>
      ) : null}
      {dock ? (letThroughHere ? (
        // A board's sessions whose findings the standards let through can be
        // audited here, as the welcome page audits its search's.
        <span className="flex flex-wrap items-center gap-[8px] px-[9px] py-[6px]">
          <HoverTooltip label="Digs a few of the findings on this workspace the standards let through (slipped past, allowed, stayed contained), at random, and the rare acts nobody has, to see whether a person should have seen them." placement="top" wide>
            <button type="button" data-case-audit="" onClick={onAudit} className={ACTION_CHIP}>Audit what the standards let through here</button>
          </HoverTooltip>
          <span className={NOTE_CLASS}>{plural(letThroughHere, "finding")}</span>
        </span>
      ) : null) : null}
    </div>
  );
}

/**
 * The cases over a search of their own: `range`, the reads' bounds
 * (toApiRange); `steer`, the search (a workspace's: its board's sessions);
 * the rest as CaseInboxBody takes them.
 */
export default function CaseInbox({ mode = "page", scopePath = null, range = null, steer = "", ...rest }) {
  const caps = useMyPermissions();
  const path = useMemo(() => scopePath || [], [scopePath]);
  const t = useTriageReads({ scopePath: path, range, steer, caps, publish: mode !== "dock" });
  return <CaseInboxBody t={t} mode={mode} {...rest} />;
}
